import { z } from 'zod';
import { nicknameProblem, type NicknameProblem } from './challenge.ts';
import { foldText } from './normalize.ts';

/*
 * «De cirio a hermano mayor»: the contract between the API and the web for accounts and
 * the game (docs/simulator-accounts.md). Texts the player accepts are versioned here, so
 * the proof of consent stores exactly what was shown.
 */

/** Version of the privacy policy and terms (provisional, to be reviewed by a lawyer). */
export const SIM_POLICY_VERSION = '2026-11-provisional';

export const SIM_CONSENT_TEXTS = {
  terms:
    'He leído y acepto las condiciones de uso y la política de privacidad de «De cirio a hermano mayor».',
  marketing:
    'Quiero recibir por correo novedades del juego, de Cruz de IA y del proyecto. Puedo darme de baja cuando quiera.',
} as const;

/** Days a brotherhood can go out, in order (the engine's PROCESSION_DAYS). */
export const SIM_PROCESSION_DAYS = [
  'Viernes de Dolores',
  'Sábado de Pasión',
  'Domingo de Ramos',
  'Lunes Santo',
  'Martes Santo',
  'Miércoles Santo',
  'Jueves Santo',
  'Madrugá',
  'Viernes Santo',
  'Sábado Santo',
  'Domingo de Resurrección',
] as const;

export const SIM_LIMITS = {
  nickname: { min: 3, max: 20 },
  password: { min: 10, max: 128 },
  email: { max: 254 },
} as const;

/** Same nickname for case, accents and spaces: "Paco", "paco" and "Pa có" are one. */
export const simNicknameKey = (nickname: string): string => foldText(nickname).replace(/ /g, '');

/** Emails are unique ignoring case and surrounding spaces. */
export const simEmailKey = (email: string): string => email.trim().toLowerCase();

export type SimNicknameProblem = NicknameProblem;

export function simNicknameProblem(raw: string): SimNicknameProblem | null {
  const nickname = raw.trim().replace(/\s+/g, ' ');
  if (nickname.length < SIM_LIMITS.nickname.min) return 'too_short';
  if (nickname.length > SIM_LIMITS.nickname.max) return 'too_long';
  if (simNicknameKey(nickname).length < SIM_LIMITS.nickname.min) return 'invalid_characters';
  return nicknameProblem(nickname);
}

export const SIM_NICKNAME_MESSAGES: Record<SimNicknameProblem | 'taken', string> = {
  too_short: `El apodo debe tener al menos ${SIM_LIMITS.nickname.min} caracteres.`,
  too_long: `El apodo puede tener como mucho ${SIM_LIMITS.nickname.max} caracteres.`,
  invalid_characters: 'Usa letras, números, espacios o . - _',
  offensive: 'Elige otro apodo, por favor.',
  taken: 'Ese apodo ya lo tiene otro hermano. Prueba con otro.',
};

export type SimPasswordProblem = 'too_short' | 'too_long' | 'common' | 'personal';

export const SIM_PASSWORD_MESSAGES: Record<SimPasswordProblem, string> = {
  too_short: `La contraseña debe tener al menos ${SIM_LIMITS.password.min} caracteres.`,
  too_long: `La contraseña puede tener como mucho ${SIM_LIMITS.password.max} caracteres.`,
  common: 'Esa contraseña es de las más usadas y se adivina enseguida. Elige otra.',
  personal: 'La contraseña no puede contener tu apodo ni tu correo.',
};

const nickname = z
  .string()
  .transform((s) => s.trim().replace(/\s+/g, ' '))
  .superRefine((s, ctx) => {
    const problem = simNicknameProblem(s);
    if (problem) ctx.addIssue({ code: 'custom', message: problem });
  });

const email = z
  .string()
  .trim()
  .max(SIM_LIMITS.email.max)
  .pipe(z.email({ message: 'invalid_email' }));

export const simRegisterSchema = z.object({
  email,
  password: z.string().max(SIM_LIMITS.password.max),
  nickname,
  /** The terms must be accepted to play */
  accept_terms: z.literal(true),
  /** Separate, unticked by default: the account works the same without it */
  marketing: z.boolean().default(false),
});
export type SimRegisterRequest = z.input<typeof simRegisterSchema>;

export const simLoginSchema = z.object({
  email: z.string().trim().max(SIM_LIMITS.email.max),
  password: z.string().max(SIM_LIMITS.password.max),
});

export const simTokenSchema = z.object({ token: z.string().min(20).max(200) });
export const simEmailSchema = z.object({ email });
export const simResetSchema = z.object({
  token: z.string().min(20).max(200),
  password: z.string().max(SIM_LIMITS.password.max),
});
export const simPreferencesSchema = z.object({ marketing: z.boolean() });
export const simDeleteSchema = z.object({ password: z.string().max(SIM_LIMITS.password.max) });
export const simUnsubscribeSchema = z.object({ key: z.string().min(20).max(200) });

export const simBrotherhoodSchema = z.object({
  name: z.string().max(200),
  popular_name: z
    .string()
    .max(80)
    .nullish()
    .transform((s) => (s?.trim() ? s.trim() : null)),
  day: z.string(),
  church: z.string().max(120),
});
export type SimBrotherhoodRequest = z.input<typeof simBrotherhoodSchema>;

export const simChoiceSchema = z.object({ choice: z.number().int().min(0).max(2) });

/** The account as the web sees it. Never includes the password or tokens. */
export interface SimMe {
  /** Playing without an account yet: no nickname, email or password */
  guest: boolean;
  nickname: string | null;
  email: string | null;
  email_verified: boolean;
  marketing: boolean;
  created_at: string;
  career_id: string | null;
}

export type SimErrorCode =
  | 'invalid_request'
  | 'unauthorized'
  | 'csrf'
  | 'email_taken'
  | 'nickname_taken'
  | 'nickname_invalid'
  | 'password_invalid'
  | 'invalid_credentials'
  | 'locked'
  | 'token_invalid'
  | 'brotherhood_invalid'
  | 'career_exists'
  | 'no_career'
  | 'not_waiting'
  /** Today's tramo is not open (no event yet today, or already played) */
  | 'tramo_closed'
  | 'daily_closed'
  | 'brotherhood_full'
  | 'too_many'
  | 'not_found'
  /** Guests sign up to do this (share a card, for one) */
  | 'register_required';

// ------------------------------------------------------------------------ game views

export type SimStatKey = 'devotion' | 'prestige' | 'board' | 'stamina' | 'wallet';

/** Moments of the career worth a share card. */
export type SimTimelineKind =
  | 'promotion'
  | 'achievement'
  | 'milestone'
  | 'election'
  | 'substitution'
  | 'start'
  | 'end'
  /** A favor returned, a reconciliation, a feud */
  | 'story'
  /** A stroke of luck, or of bad luck */
  | 'rare'
  /** The junta took a post away */
  | 'demotion';

/** The day so far: every stat (0 if untouched) and the brotherhood. */
export interface SimDayProgress {
  stats: { key: SimStatKey; label: string; delta: number }[];
  brotherhood: { members: number; prestige: number; treasury: number };
}

export interface SimNextStep {
  rank_label: string;
  /** Hermano mayor: elected in a cabildo every few years */
  by_election: boolean;
  years: { need: number; have: number };
  stats: { key: SimStatKey; label: string; need: number; have: number }[];
  ready: boolean;
  /** 0 … 100: how far along (100 only when ready) */
  progress: number;
}

/** A vote of the cabildo: a post the player is up for, or the elections to hermano mayor. */
export interface SimVote {
  kind: 'promotion' | 'election';
  /** "Insignia", "Hermano mayor" */
  target_label: string;
  /** 0 … 100: the odds of the post, or the share of votes in the elections */
  chance: number;
}

/** Someone in an event, to remember who is who. */
export interface SimCastMember {
  id: string;
  name: string;
  /** "tu amigo de toda la vida", "la priosta" */
  role: string;
  description: string;
  /** −10 … +10 */
  relation: number;
  /** Comic portrait (a path of the web), the same all the career */
  portrait: string | null;
}

/** A career as the web sees it: no seeds, flags or library ids. */
export interface SimCareerView {
  id: string;
  finished: boolean;
  brotherhood: {
    name: string;
    popular_name: string | null;
    day: string;
    church: string;
    members: number;
    nazarenes: number;
    prestige: number;
    treasury: number;
    milestones: { key: string; label: string }[];
  };
  player: {
    member_number: number;
    seniority_years: number;
    rank: string;
    rank_label: string;
    post: string | null;
    position: number;
    tramo: number | null;
    tramos: number;
    processions: number;
    stats: { key: SimStatKey; label: string; value: number }[];
    /** The next post and what it asks for; null for the hermano mayor */
    next_step: SimNextStep | null;
    /** The post is at risk: stats fallen too low, and events left to recover them */
    risk: {
      rank_label: string;
      events_left: number;
      stats: { key: SimStatKey; label: string; have: number; floor: number }[];
    } | null;
  };
  time: {
    year: number;
    phase: string;
    phase_label: string;
    streak: number;
    best_streak: number;
    /** A missed day was forgiven today (one a week) */
    streak_saved: boolean;
    days_played: number;
    mandate_left: number | null;
  };
  /** The cabildo votes tomorrow on this (null when nothing is pending) */
  vote: SimVote | null;
  today: {
    day: string;
    events_per_day: number;
    events_left: number;
    next_day_at: string | null;
    holy_week: { starts_at: string; on_now: boolean };
    /** What today's answered events changed, added up */
    progress: SimDayProgress;
    /** Today's errand and the week's goals (null once the career is over) */
    errands: SimErrands | null;
    /** «Ordena el tramo»: open today, or how it went (null before the first event) */
    tramo: SimTramoToday | null;
  };
  characters: {
    id: string;
    name: string;
    role: string;
    description: string;
    /** −10 … +10 */
    relation: number;
    portrait: string | null;
    /** What is pending with them: they owe you one, or something still hurts */
    thread: 'favor' | 'grudge' | null;
  }[];
  /** The whole catalogue: the ones not unlocked yet show as empty frames */
  achievements: {
    id: string;
    name: string;
    description: string;
    unlocked: boolean;
    year: number | null;
    shareable: boolean;
  }[];
  timeline: {
    /** Position in the career's timeline: the `ref` to share it */
    index: number;
    year: number;
    phase_label: string;
    kind: SimTimelineKind;
    text: string;
    shareable: boolean;
  }[];
}

/** An event to answer: the text and the options, never what each option does. */
export interface SimEventView {
  idx: number;
  /** Yesterday's cliffhanger, when this event resolves it */
  from_yesterday: string | null;
  title: string;
  text: string;
  options: { index: number; text: string }[];
  last_of_day: boolean;
  /** Who appears, in order of importance */
  cast: SimCastMember[];
  /** A favor or a grudge coming back: "Hace 3 días, en «…», elegiste «…»." */
  callback: string | null;
  /** A rare event: a stroke of luck (boon) or of bad luck (blow) */
  rare: 'boon' | 'blow' | null;
  /** This event tells how yesterday's vote went: the web shows the count first */
  verdict: (SimVote & { success: boolean }) | null;
}

/** What a choice changed, ready to show. */
export interface SimChangesView {
  stats: { key: SimStatKey; label: string; delta: number }[];
  relation: { name: string; delta: number } | null;
  brotherhood: { members: number; prestige: number; treasury: number };
  promoted: { rank_label: string; post: string | null } | null;
  /** The junta took the post away */
  demoted: { lost_label: string; rank_label: string } | null;
  /** The post is at risk from now (warning), or safe again (cleared) */
  risk: 'warning' | 'cleared' | null;
  achievements: { id: string; name: string; description: string; shareable: boolean }[];
  year_ended: boolean;
  /** Someone will remember this choice: "A Carmen le ha llegado. Se acordará de esto." */
  memory: { kind: 'favor' | 'grudge'; name: string; text: string } | null;
  /** A favor or a grudge that ended with this choice */
  thread_end: { end: 'collected' | 'kept' | 'mended' | 'feud'; name: string; text: string } | null;
  /** Today's errand or a weekly goal completed with this choice */
  errands: { kind: 'daily' | 'weekly'; text: string; reward: string }[];
}

/** A short goal for today and three for the week, with their rewards. */
export interface SimErrands {
  daily: {
    text: string;
    character_id: string;
    name: string;
    role: string;
    portrait: string | null;
    done: boolean;
    reward: string;
  } | null;
  weekly: { id: string; text: string; have: number; need: number; done: boolean; reward: string }[];
}

/** Today's «Ordena el tramo», as the day screen shows it. */
export interface SimTramoToday {
  available: boolean;
  level: number;
  /** Stars of today's game, once played */
  stars: number | null;
  /** Best time with the tramo complete, in ms */
  best_ms: number | null;
  /** Today's game day, once played with the tramo complete: it can go to friends as a challenge */
  challenge_day: string | null;
}

export interface SimTramoPerson {
  id: string;
  name: string;
  number: number;
  kind: 'nazareno' | 'insignia' | 'child' | 'cross';
  gender: 'm' | 'f';
  me: boolean;
  /** Insignia carried, or who a child walks with */
  note: string | null;
}

/** Today's tramo to put in order. The order is in it: the score is checked by the server. */
export interface SimTramoPuzzle {
  level: number;
  seconds: number;
  title: string;
  intro: string;
  rules: string[];
  people: SimTramoPerson[];
  order: string[];
  latecomer: { id: string; after: number } | null;
  deputy: { name: string; portrait: string | null } | null;
}

export const simTramoPlaySchema = z.object({
  /** Every card tapped, in order, wrong ones included */
  taps: z.array(z.string().regex(/^(me|p\d{1,2})$/)).max(200),
  ms: z.number().int().min(0).max(600_000),
});
export type SimTramoPlayRequest = z.infer<typeof simTramoPlaySchema>;

export interface SimTramoResponse {
  result: {
    stars: number;
    errors: number;
    placed: number;
    total: number;
    complete: boolean;
    ms: number;
    best: boolean;
    level: number;
    /** The game day, to share the tramo as a challenge */
    day: string;
    changes: SimChangesView;
  };
  career: SimCareerView;
}

export interface SimEventResponse {
  event: SimEventView | null;
  career: SimCareerView;
}

export interface SimChoiceResponse {
  outcome: string;
  changes: SimChangesView;
  /** The cliffhanger left for tomorrow, on the last event of the day */
  hook: string | null;
  day_done: boolean;
  career: SimCareerView;
}

export interface SimBrotherhoodProblem {
  field: 'name' | 'popular_name' | 'day' | 'church' | 'all';
  reason: string;
  message: string;
}

export interface SimBrotherhoodCheck {
  ok: boolean;
  problems: SimBrotherhoodProblem[];
}

// --------------------------------------------------------------------------- shares

export const simShareSchema = z.object({
  kind: z.enum(['achievement', 'moment', 'decision', 'tramo']),
  /**
   * The achievement id, the timeline index of the moment, the index of the event or, for
   * a tramo challenge, the game day it was played
   */
  ref: z.string().min(1).max(60),
});
export type SimShareRequest = z.infer<typeof simShareSchema>;

export const SIM_SHARE_ID = /^[a-z2-9]{10}$/;

/** A friend's vote on a shared decision. `voter` is a random id kept in their browser. */
export const simVoteSchema = z.object({
  choice: z.number().int().min(0).max(2),
  voter: z.string().regex(/^[A-Za-z0-9_-]{16,64}$/),
});
export type SimVoteRequest = z.infer<typeof simVoteSchema>;

/** A friend plays a shared tramo: the same fields as the player's game, and their browser id. */
export const simChallengePlaySchema = simTramoPlaySchema.extend({
  voter: simVoteSchema.shape.voter,
});
export type SimChallengePlayRequest = z.infer<typeof simChallengePlaySchema>;

export interface SimTramoScore {
  stars: number;
  errors: number;
  ms: number;
  complete: boolean;
}

/** A tramo challenge, once played (or to whoever shared it). */
export interface SimChallengeResults {
  /** How the player who shared it did */
  owner: SimTramoScore;
  /** How you did, when you have played */
  mine: SimTramoScore | null;
  /** Yours against theirs: complete first, then mistakes, then time */
  outcome: 'win' | 'draw' | 'lose' | null;
  /** Friends who have played it, and how many of them did better */
  tries: number;
  beaten: number;
}

/** «El tramo del día»: the same tramo for everyone, played once without an account. */
export const simDailyPlaySchema = simChallengePlaySchema.extend({
  /** The game day it was opened on (it may end after the day changes) */
  day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export type SimDailyPlayRequest = z.infer<typeof simDailyPlaySchema>;

export interface SimDailyResults {
  /** «Tramo del día» number: 1 on the first day */
  number: number;
  day: string;
  mine: SimTramoScore;
  /** One square per person: 🟣 first try, 🟡 one mistake, 🔴 more, ⚪ unplaced */
  grid: string;
  /** Everyone who has played it so far, you included */
  players: number;
  /** Of them, with three stars (0–100) */
  perfect_pct: number;
  /** Of the others, how many you beat (0–100); null when you are the first */
  beat_pct: number | null;
}

export interface SimDailyResponse {
  number: number;
  day: string;
  /** The tramo, until you play it */
  puzzle: SimTramoPuzzle | null;
  /** How you did and how the rest did, once played */
  results: SimDailyResults | null;
}

/** Starting as a guest, maybe from a shared card. */
export const simGuestSchema = z.object({ ref: z.string().regex(SIM_SHARE_ID).optional() });

/** What a shared decision reveals once you have voted (or to whoever shared it). */
export interface SimDecisionResults {
  /** The option the player chose */
  chosen: number;
  /** What happened to them */
  outcome: string;
  /** Friends' votes for each option */
  votes: number[];
  total: number;
  /** Your vote, when you have voted */
  mine: number | null;
}

/** A share card: a frozen snapshot, with nothing personal but the nickname. */
export interface SimShareCard {
  id: string;
  nickname: string;
  /** Popular name, or the full name when there is none */
  brotherhood: string;
  rank_label: string;
  seniority_years: number;
  member_number: number;
  /** Small line above the title: "Logro", "Ascenso" */
  kicker: string;
  /** "Fiscal de paso", "Estreno de túnica" */
  title: string;
  /** In first person: "Me han nombrado fiscal de paso tras 18 años de hermano." */
  phrase: string;
  created_at: string;
  /** Absent in cards from before decisions */
  kind?: 'achievement' | 'moment' | 'decision' | 'tramo';
  /** A shared decision: the situation and the options (what was chosen comes on voting) */
  decision?: { text: string; options: string[] };
  /** A tramo challenge: how the player did */
  tramo?: { level: number } & SimTramoScore;
}

export interface SimShareResponse {
  card: SimShareCard;
  /** Public page of the card (/simulador/logro/:id) */
  url: string;
  /** 1080 × 1920, for WhatsApp and Instagram stories */
  image_story: string;
  /** 1200 × 630, for link previews */
  image_wide: string;
  /** A decision seen by the player who shared it: what friends voted */
  results?: SimDecisionResults | null;
  /** A tramo challenge: the tramo to play, until played, and the results after */
  challenge?: { puzzle: SimTramoPuzzle | null; results: SimChallengeResults | null };
}

// ----------------------------------------------------------- shared brotherhoods

/** Players in one brotherhood at most: a group of friends, not a forum. */
export const SIM_MAX_BROTHERS = 30;

/** A player in the same brotherhood. */
export interface SimBrother {
  /** Null for a guest who has not saved the career yet */
  nickname: string | null;
  rank_label: string;
  seniority_years: number;
  member_number: number;
  /** 0 (cirio) … 6 (hermano mayor): for the standings */
  rank_index: number;
  /** 0 … 100 towards their next post (100 for the hermano mayor) */
  progress: number;
  played_today: boolean;
  /** Their career is over */
  finished: boolean;
  you: boolean;
  founder: boolean;
}

/** The player's brotherhood as friends see it: who plays in it and the invitation. */
export interface SimMyBrotherhood {
  /** The invitation, once opened to friends */
  code: string | null;
  url: string | null;
  /** By member number; only you until someone joins */
  brothers: SimBrother[];
  max: number;
}

/** The public page of an invitation (/simulador/hermandad/:code). */
export interface SimBrotherhoodInvite {
  code: string;
  url: string;
  name: string;
  popular_name: string | null;
  day: string;
  church: string;
  /** Who opened it to friends */
  founder: string | null;
  /** Who plays in it, by member number (nicknames only) */
  brothers: { nickname: string | null; rank_label: string }[];
  full: boolean;
  /** The visitor already plays in it */
  member: boolean;
  /** The visitor has a career going in another brotherhood */
  busy: boolean;
}

// -------------------------------------------------------------------------- reports

export const SIM_REPORT_REASONS = {
  offensive: 'Es ofensivo o de mal gusto',
  faith: 'Falta al respeto a la fe',
  real: 'Habla de personas o hermandades reales',
  nonsense: 'No tiene sentido o tiene errores',
  other: 'Otra cosa',
} as const;
export type SimReportReason = keyof typeof SIM_REPORT_REASONS;

const reportFields = {
  reason: z.enum(Object.keys(SIM_REPORT_REASONS) as [SimReportReason, ...SimReportReason[]]),
  comment: z
    .string()
    .max(500)
    .nullish()
    .transform((s) => (s?.trim() ? s.trim() : null)),
};

/** A player reports one of their events (by its index in the career). */
export const simEventReportSchema = z.object({ idx: z.number().int().min(0), ...reportFields });
/** Anyone reports a shared card. */
export const simShareReportSchema = z.object(reportFields);

// ------------------------------------------------------------- review panel

export interface SimSwitches {
  /** Any AI at all (live events, review, diary) */
  ai: boolean;
  /** Events written live (off: library only, review and diary still on) */
  ai_live: boolean;
  /** Euros a day for AI before playing from the library only */
  daily_budget_eur: number;
  /** Reports from different players that retire a library event or hide a card */
  report_threshold: number;
}

export interface SimSwitchesView extends SimSwitches {
  /** Forced off by the environment: the panel cannot change them */
  locked: { ai: boolean; ai_live: boolean };
}

/** One thing reported, with all its open reports (what the panel shows). */
export interface SimReportGroup {
  key: string;
  target: 'event' | 'share';
  /** Library event: retiring it removes it for everyone */
  library_id: string | null;
  share_id: string | null;
  /** The library event is retired / the card is hidden now */
  removed: boolean;
  snapshot: unknown;
  reports: { reason: string; comment: string | null; created_at: string; signed_in: boolean }[];
  first_at: string;
}

export interface SimDayMetrics {
  day: string;
  /** Accounts made that day (guests who signed up count on the day they did) */
  signups: number;
  /** Players who started as guests that day */
  guests: number;
  /** Of them, those who played the whole first day */
  guests_day_one: number;
  /** Of them, those who signed up (so far) */
  guests_converted: number;
  guest_conversion: number;
  active_players: number;
  events: number;
  events_per_player: number;
  ai_cost_eur: number;
  cost_per_player_eur: number;
  reviews: number;
  rejected: number;
  rejection_rate: number;
  reports: number;
  shares: number;
  /** Visits to the public pages of cards */
  share_visits: number;
  /** Friends' votes on shared decisions */
  votes: number;
  /** New players who came from a card */
  referred: number;
  /** «Ordena el tramo» played, and of them with three stars */
  tramos: number;
  tramos_perfect: number;
  /** Friends who played a tramo challenge */
  challenges: number;
  /** «Tramo del día» played */
  daily_tramos: number;
  /** Players who joined a friend's brotherhood */
  joins: number;
}

export interface SimRetention {
  /** Players whose day N after signing up is already over */
  eligible: number;
  /** Of them, those who played on that day */
  retained: number;
  rate: number;
}

export interface SimMetrics {
  today: string;
  days: SimDayMetrics[];
  retention: { d1: SimRetention; d7: SimRetention; d30: SimRetention };
  totals: {
    users: number;
    /** Guests playing now without an account */
    guests: number;
    careers: number;
    finished_careers: number;
    marketing_rate: number;
    shares: number;
    open_reports: number;
    library_active: number;
    library_retired: number;
    ai_cost_eur: number;
  };
}
